import type { NetworkType, ParamTreeNode } from '@mls-wallet/shared-types';
import { HighLevelSolidityType } from '@mls-wallet/shared-types';
import { getValueTypeFromSolidityType } from '../../transaction-builder/parameter-inputs/utils';
import { RawValue } from './RawValue';
import { CornerDownRight } from 'lucide-react';
import type { AddressBookExactEntry } from '@/types/address-book';

/**
 * Convert camelCase/snake_case to human-readable (ported from den-ui/utilities)
 */
function getReadableSolidityName(name: string, fallback: string): string {
  if (!name || name === '') return fallback;
  // Convert camelCase to spaces
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
}

/**
 * Display a struct/tuple parameter with named children
 */
export function RawValueObject(props: {
  paramTreeNode: ParamTreeNode;
  networkId: NetworkType;
  contractAddress?: string;
  inListOrObject?: boolean;
  depth: number;
  methodName?: string;
  paramName?: string;
  addressBookEntryByAddress?: Map<string, AddressBookExactEntry>;
}) {
  const numChildren = props.paramTreeNode.children?.length ?? 0;

  // Calculate background opacity based on depth for progressive darkening
  const bgOpacity = Math.min(0.02 + props.depth * 0.02, 0.1);

  return (
    <div
      className={`${
        props.inListOrObject ? 'mt-2 ml-1' : ''
      } flex-1 border border-zinc-200 border-l-2 border-l-zinc-900 px-3 py-2 rounded-sm`}
      style={{
        backgroundColor: `rgba(0, 0, 0, ${bgOpacity})`,
      }}
    >
      <div className="flex flex-col gap-2">
        {/* Display each child element in the struct or tuple */}
        {props.paramTreeNode.children?.map(
          (childParamTreeNode: ParamTreeNode, index: number) => (
            <div
              key={index}
              className={`${
                numChildren - 1 !== index
                  ? 'pb-2 border-b border-zinc-200 dark:border-zinc-800'
                  : ''
              }`}
            >
              {/* Case: child element has a name (struct field) */}
              {childParamTreeNode.structParamName && (
                <div className="flex items-start justify-between mb-1">
                  <div className="text-sm text-zinc-500">
                    {getReadableSolidityName(
                      childParamTreeNode.structParamName,
                      'Parameter'
                    )}
                    {/* Case: child is an array, show element count */}
                    {getValueTypeFromSolidityType(
                      childParamTreeNode.rawType
                    ) === HighLevelSolidityType.ARRAY &&
                      childParamTreeNode.children !== undefined && (
                        <span className="ml-1">
                          ({childParamTreeNode.children.length} item
                          {childParamTreeNode.children.length === 1 ? '' : 's'})
                        </span>
                      )}
                  </div>
                </div>
              )}

              <div className="flex items-start">
                {/* Case: child is array or struct/tuple, show arrow */}
                {[
                  HighLevelSolidityType.STRUCT_OR_TUPLE,
                  HighLevelSolidityType.ARRAY,
                ].includes(childParamTreeNode.highLevelType) && (
                  <CornerDownRight className="w-4 h-4 text-zinc-400 mr-2 mt-1 flex-shrink-0" />
                )}
                {/* Render child value */}
                <div className="flex-1">
                  <RawValue
                    paramTreeNode={childParamTreeNode}
                    networkId={props.networkId}
                    inListOrObject={true}
                    depth={props.depth + 1}
                    {...(childParamTreeNode.structParamName
                      ? { paramName: childParamTreeNode.structParamName }
                      : {})}
                    {...(props.methodName
                      ? { methodName: props.methodName }
                      : {})}
                    {...(props.addressBookEntryByAddress
                      ? {
                          addressBookEntryByAddress:
                            props.addressBookEntryByAddress,
                        }
                      : {})}
                  />
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
